import { SortModelItem } from "@ag-grid-community/core";

import { Unionize } from "./generic";
import { PaginatedRequest, PaginatedResponse, RequestPagination } from "./request";

export type PresignedPost = {
  /**
   * The bucket url of that must match the prefix key of any attempted upload.
   */
  Url: string;
  /**
   * The presigned post fields, including the signature and expiry information.
   */
  Fields: any;
};

export type BaseAttachment = {
  /**
   * The attachment display name and S3 file key.
   */
  Name: string;
  /**
   * The attachment file extension
   */
  Extension: string;
  /**
   * The attachment file mime type.
   */
  MimeType: string;
  /**
   * The attachment file size.
   */
  Size: number;
};

export type AttachmentPostBody = BaseAttachment & {
  /**
   * Unique identifier for the attachment project.
   */
  ProjectID: string;
  /**
   * Timestamp (UTC) of the last time the attachment file was uploaded.
   */
  UploadedAt?: string;
  /**
   * The username of the last user to upload the attachment file to S3.
   */
  UploadedBy?: string;
  /**
   * The unique identifier for a feature belonging to a layer on the map.
   */
  LayerFeatureID?: string;
  /**
   * The description for the attachment.
   */
  Description?: string;
};

export const AttachmentFilterType = ["project", "layer_feature"] as const;
export type AttachmentFilterType = Unionize<typeof AttachmentFilterType>;

export type BaseAttachmentsRequest = {
  ProjectID: string;
  LayerFeatureID?: string;
  Type?: AttachmentFilterType | null;
};

export type AttachmentsRequest = PaginatedRequest<BaseAttachmentsRequest>;

export type AttachmentsResponse = PaginatedResponse<{
  Attachments: Attachment[];
}>;

export type FeatureDetails = {
  ProjectVersionID: string;
  LayerKey: string;
  PropertyKey: string;
  PropertyValue: string;
};

export type Attachment = AttachmentPostBody & {
  /**
   * Unique identifier for the attachment.
   */
  ID: string;
  /**
   * The presigned urls to retrieve the file from S3. Available for attachments with uploaded files.
   */
  Urls?: { View: string; Download: string };
  Location?: {
    latitude?: number;
    longitude?: number;
  };
  FeatureDetails?: FeatureDetails;
  LayerFeature: {
    ID: string;
    Layer: {
      ID: string;
      LayerKey: string;
    };
  };
};

export type AttachmentWhitelist = {
  /**
   * Unique identifier for the attachment whitelist item.
   */
  ID: string;
  /**
   * The presigned url to retrieve the file from S3. Available for attachments with uploaded files.
   */
  Extension: string | null;
  /**
   * The mimetype 'Type'.
   */
  Type: string | null;
  /**
   * The mimetype 'SubType'.
   */
  Subtype: string | null;
};

export type uploadFileToS3Props = {
  presignedPost: any;
  file: File;
  onProgress: (progress: number) => void;
  onComplete: () => void;
  onError: (error: Error) => void;
};

export const AttachmentEntityType = ["Project", "Feature"] as const;
export type AttachmentEntityType = Unionize<typeof AttachmentEntityType>;

export type BulkFileProps = {
  file: File;
  featureDetails?: FeatureDetails;
};

export type GetAttachmentsParams = {
  projectId: string;
  layerFeatureId?: string | null;
  pagination?: RequestPagination;
  sortModel?: SortModelItem[];
  filterType?: AttachmentFilterType | null;
};
